import React, { useState, useRef, useEffect } from 'react';
import { Destination, PhotoDestination, DestinationUpdateRequest } from '../../types';
import { Upload, X, ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import heic2any from 'heic2any';
import { heicTo } from 'heic-to';

interface EditDestinationImagesPageProps {
  destination: Destination;
  onBack: () => void;
  onSave: (updates: DestinationUpdateRequest) => Promise<void>;
  loading: boolean;
}

export const EditDestinationImagesPage: React.FC<EditDestinationImagesPageProps> = ({
  destination,
  onBack,
  onSave,
  loading,
}) => {
  // Helper function to transform photo list
  const transformPhotoList = (list: PhotoDestination[]): PhotoDestination[] => {
    return (list || []).map((photo: PhotoDestination) => ({
      ...photo,
      url: photo.url || '',
      thumb: photo.thumb || '',
      medium: photo.medium || '',
      large: photo.large || '',
      alt: photo.alt || '',
      caption: photo.caption || '',
      credit: photo.credit || '',
    }));
  };

  const [photoList, setPhotoList] = useState<PhotoDestination[]>(transformPhotoList(destination.photos || []));
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [uploadStats, setUploadStats] = useState({ current: 0, total: 0, completed: 0, failed: 0 });
  const [saving, setSaving] = useState(false);
  const [editingUrlsIndex, setEditingUrlsIndex] = useState<number | null>(null);
  const [editingUrls, setEditingUrls] = useState<{ url: string; thumb: string; medium: string; large: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update photo list when destination changes
  useEffect(() => {
    setPhotoList(transformPhotoList(destination.photos || []));
  }, [destination]);

  // Convert HEIC/HEIF files to JPG
  const convertHeicToJpg = async (file: File): Promise<File> => {
    const isHeic = /\.(heic|heif)$/i.test(file.name) || 
                   file.type === 'image/heic' || 
                   file.type === 'image/heif';
    
    if (!isHeic) {
      return file;
    }

    if (typeof heic2any === 'function') {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92,
        });

        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        
        if (!blob) {
          throw new Error('Conversion returned empty result');
        }

        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        const convertedFile = new File([blob], fileName, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });

        return convertedFile;
      } catch (error: any) {
        const errorCode = error?.code;
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        
        if (errorCode === 2 || errorMessage.includes('ERR_LIBHEIF format not supported') || errorMessage.includes('format not supported')) {
          console.log('heic2any failed, trying heic-to as fallback...');
        } else {
          throw error;
        }
      }
    }

    if (typeof heicTo === 'function') {
      try {
        const convertedBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.92,
        });

        if (!convertedBlob) {
          throw new Error('Conversion returned empty result');
        }

        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        const convertedFile = new File([convertedBlob], fileName, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });

        return convertedFile;
      } catch (error: any) {
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        console.error('Error converting HEIC to JPG with heic-to:', {
          error,
          message: errorMessage,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });
        
        toast.error(
          'Failed to convert HEIC file. This format may not be supported. Please convert it to JPG using your device\'s Photos app or an online converter before uploading.',
          { duration: 6000 }
        );
        throw error;
      }
    }

    toast.error('HEIC conversion is not available. Please convert the file manually.');
    throw new Error('HEIC conversion libraries not available');
  };

  const handleImageUpload = async (file: File) => {
    const isImageByType = file.type.startsWith('image/');
    const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);
    
    if (!isImageByType && !isImageByExtension) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 30 * 1024 * 1024) {
      toast.error('Image size should be less than 30MB');
      return;
    }

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      const fileToUpload = await convertHeicToJpg(file);

      // Use uploadToImageCloudinary for transformation URLs
      const result = await apiService.uploadToImageCloudinary(fileToUpload, (progress) => {
        setUploadProgress(progress);
      });

      const fileExt = fileToUpload.name.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Generate transformation URLs
      const thumb = `https://img.bozapp.in/t/w_400/${result.id}.${fileExt}`;
      const medium = `https://img.bozapp.in/t/w_800/${result.id}.${fileExt}`;
      const large = `https://img.bozapp.in/t/w_1200/${result.id}.${fileExt}`;

      // Get image dimensions
      const img = new Image();
      img.src = result.original_url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const newPhoto: PhotoDestination = {
        url: result.original_url,
        thumb: thumb,
        medium: medium,
        large: large,
        alt: '',
        caption: '',
        credit: '',
        isHero: false,
        type: 'DESTINATION',
        width: img.width,
        height: img.height,
        uploadedOn: Date.now(),
      };

      setPhotoList((prev) => [...prev, newPhoto]);
      toast.success('Image uploaded successfully');
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleMultipleImageUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    fileArray.forEach((file) => {
      const isImageByType = file.type.startsWith('image/');
      const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);
      
      if (!isImageByType && !isImageByExtension) {
        errors.push(`${file.name}: Not an image file`);
        return;
      }
      if (file.size > 30 * 1024 * 1024) {
        errors.push(`${file.name}: Size exceeds 30MB`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      toast.error(`Some files were skipped:\n${errors.join('\n')}`);
    }

    if (validFiles.length === 0) {
      return;
    }

    try {
      setUploadingMultiple(true);
      setUploadStats({
        current: 0,
        total: validFiles.length,
        completed: 0,
        failed: 0,
      });

      const uploadedPhotos: PhotoDestination[] = [];
      let failedCount = 0;
      let completedCount = 0;

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadStats((prev) => ({
          ...prev,
          current: i + 1,
        }));

        try {
          const fileToUpload = await convertHeicToJpg(file);

          const result = await apiService.uploadToImageCloudinary(fileToUpload, (progress) => {
            const fileProgress = progress / 100;
            const overallProgress = ((i + fileProgress) / validFiles.length) * 100;
            setUploadProgress(overallProgress);
          });

          const fileExt = fileToUpload.name.split('.').pop()?.toLowerCase() || 'jpg';
          
          const thumb = `https://img.bozapp.in/t/w_400/${result.id}.${fileExt}`;
          const medium = `https://img.bozapp.in/t/w_800/${result.id}.${fileExt}`;
          const large = `https://img.bozapp.in/t/w_1200/${result.id}.${fileExt}`;

          const img = new Image();
          img.src = result.original_url;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          const newPhoto: PhotoDestination = {
            url: result.original_url,
            thumb: thumb,
            medium: medium,
            large: large,
            alt: '',
            caption: '',
            credit: '',
            isHero: false,
            type: 'DESTINATION',
            width: img.width,
            height: img.height,
            uploadedOn: Date.now(),
          };

          uploadedPhotos.push(newPhoto);
          completedCount++;
          setUploadStats((prev) => ({
            ...prev,
            completed: completedCount,
          }));
        } catch (error: any) {
          console.error(`Failed to upload ${file.name}:`, error);
          failedCount++;
          setUploadStats((prev) => ({
            ...prev,
            failed: failedCount,
          }));
        }
      }

      if (uploadedPhotos.length > 0) {
        setPhotoList((prev) => [...prev, ...uploadedPhotos]);
        toast.success(`Successfully uploaded ${uploadedPhotos.length} image${uploadedPhotos.length > 1 ? 's' : ''}`);
      }

      if (failedCount > 0) {
        toast.error(`${failedCount} image${failedCount > 1 ? 's' : ''} failed to upload`);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploadingMultiple(false);
      setUploadProgress(0);
      setUploadStats({ current: 0, total: 0, completed: 0, failed: 0 });
    }
  };

  const removePhoto = (index: number) => {
    setPhotoList((prev) => prev.filter((_, i) => i !== index));
    toast.success('Photo removed');
  };

  const updatePhotoInfo = (index: number, field: keyof PhotoDestination, value: any) => {
    setPhotoList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const toggleHeroPhoto = (index: number) => {
    setPhotoList((prev) => {
      const updated = prev.map((photo, i) => ({
        ...photo,
        isHero: i === index ? !photo.isHero : false, // Only one hero photo
      }));
      return updated;
    });
    toast.success('Hero photo updated');
  };

  const openUrlEditor = (index: number) => {
    const photo = photoList[index];
    setEditingUrlsIndex(index);
    setEditingUrls({
      url: photo.url || '',
      thumb: photo.thumb || '',
      medium: photo.medium || '',
      large: photo.large || '',
    });
  };

  const closeUrlEditor = () => {
    setEditingUrlsIndex(null);
    setEditingUrls(null);
  };

  const saveUrlsFromModal = () => {
    if (editingUrlsIndex === null || !editingUrls) return;

    const photo = photoList[editingUrlsIndex];
    
    setPhotoList((prev) => {
      const updated = [...prev];
      updated[editingUrlsIndex] = {
        ...photo,
        url: editingUrls.url,
        thumb: editingUrls.thumb,
        medium: editingUrls.medium,
        large: editingUrls.large,
      };
      return updated;
    });

    toast.success('URLs updated successfully');
    closeUrlEditor();
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates: DestinationUpdateRequest = {
        photos: photoList,
      };

      await onSave(updates);
      toast.success('Photos saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save photos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={saving || loading}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Edit Images - {destination.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage photos for this destination
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading || uploadingImage || uploadingMultiple}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Upload Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Upload New Photo
              </h2>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    if (files.length === 1) {
                      handleImageUpload(files[0]);
                    } else {
                      handleMultipleImageUpload(files);
                    }
                  }
                }}
                className="hidden"
                disabled={loading || uploadingImage || uploadingMultiple}
              />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
              >
                {(uploadingImage || uploadingMultiple) ? (
                  <>
                    <div className="w-full max-w-md space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            {uploadingMultiple ? (
                              <>Uploading {uploadStats.current} of {uploadStats.total}</>
                            ) : (
                              <>Uploading...</>
                            )}
                          </span>
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-medium">
                          {Math.round(uploadProgress)}%
                        </span>
                      </div>
                      
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      
                      {uploadingMultiple && (
                        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>✓ Completed: {uploadStats.completed}</span>
                          {uploadStats.failed > 0 && (
                            <span className="text-red-600 dark:text-red-400">✗ Failed: {uploadStats.failed}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-full">
                      <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        PNG, JPG, GIF, HEIC up to 30MB each (Multiple files supported)
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Select Photos
                    </button>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Photos Grid */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                All Photos ({photoList.length})
              </h2>
            </div>

            {photoList.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No photos uploaded yet. Upload your first photo above.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {photoList.map((photo, index) => (
                  <div
                    key={index}
                    className={`border-2 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 transition-all ${
                      photo.isHero
                        ? 'border-yellow-500 ring-2 ring-yellow-200 dark:ring-yellow-800'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {/* Photo */}
                    <div className="relative aspect-video bg-gray-200 dark:bg-gray-800">
                      <img
                        src={photo.thumb || photo.url}
                        alt={photo.alt || photo.caption || `Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.src !== photo.url) {
                            e.currentTarget.src = photo.url;
                          } else {
                            e.currentTarget.style.display = 'none';
                          }
                        }}
                      />
                      
                      {/* Hero Badge */}
                      {photo.isHero && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-600 text-white text-xs font-medium rounded">
                          Hero Photo
                        </div>
                      )}

                      {/* Actions */}
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => toggleHeroPhoto(index)}
                          className={`p-1.5 rounded transition-colors ${
                            photo.isHero
                              ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                          disabled={loading || saving}
                          title={photo.isHero ? 'Remove as Hero' : 'Set as Hero Photo'}
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          disabled={loading || saving}
                          title="Delete Photo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Photo Info */}
                    <div className="p-4 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Alt Text
                        </label>
                        <input
                          type="text"
                          value={photo.alt || ''}
                          onChange={(e) => updatePhotoInfo(index, 'alt', e.target.value)}
                          placeholder="Enter alt text"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading || saving}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Caption
                        </label>
                        <input
                          type="text"
                          value={photo.caption || ''}
                          onChange={(e) => updatePhotoInfo(index, 'caption', e.target.value)}
                          placeholder="Enter caption"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading || saving}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Credit
                        </label>
                        <input
                          type="text"
                          value={photo.credit || ''}
                          onChange={(e) => updatePhotoInfo(index, 'credit', e.target.value)}
                          placeholder="Enter credit"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading || saving}
                        />
                      </div>

                      <div className="pt-2 border-t border-gray-200 dark:border-gray-600 space-y-2">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          URLs
                        </div>
                        {[
                          { label: 'URL', field: 'url' as keyof typeof photo, value: photo.url },
                          { label: 'Thumb', field: 'thumb' as keyof typeof photo, value: photo.thumb || '' },
                          { label: 'Medium', field: 'medium' as keyof typeof photo, value: photo.medium || '' },
                          { label: 'Large', field: 'large' as keyof typeof photo, value: photo.large || '' },
                        ].map(({ label, value }) => (
                          <div key={label} className="space-y-1">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                              {label}
                            </label>
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={value}
                                onFocus={() => openUrlEditor(index)}
                                readOnly
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white font-mono cursor-pointer hover:border-blue-400 transition-colors"
                                placeholder={`Enter ${label.toLowerCase()}`}
                                disabled={loading || saving}
                                title="Click to edit all URLs in popup"
                              />
                              {value && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(value);
                                      toast.success(`${label} copied to clipboard`);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                    title={`Copy ${label}`}
                                    disabled={loading || saving}
                                  >
                                    Copy
                                  </button>
                                  <a
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                    title={`Open ${label} in new tab`}
                                  >
                                    Open
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* URL Editor Modal */}
      {editingUrlsIndex !== null && editingUrls && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit URLs - Photo {editingUrlsIndex + 1}
              </h2>
              <button
                onClick={closeUrlEditor}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={loading || saving}
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {[
                { label: 'URL', field: 'url' as keyof typeof editingUrls, description: 'Original/Full size image URL' },
                { label: 'Thumb URL', field: 'thumb' as keyof typeof editingUrls, description: 'Thumbnail transformation (400px width)' },
                { label: 'Medium URL', field: 'medium' as keyof typeof editingUrls, description: 'Medium transformation (800px width)' },
                { label: 'Large URL', field: 'large' as keyof typeof editingUrls, description: 'Large transformation (1200px width)' },
              ].map(({ label, field, description }) => (
                <div key={field} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {label}
                    </label>
                    {editingUrls[field] && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(editingUrls[field]);
                            toast.success(`${label} copied to clipboard`);
                          }}
                          className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                          Copy
                        </button>
                        <a
                          href={editingUrls[field]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        >
                          Open
                        </a>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
                  <textarea
                    value={editingUrls[field]}
                    onChange={(e) => setEditingUrls({ ...editingUrls, [field]: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    disabled={loading || saving}
                  />
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeUrlEditor}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={loading || saving}
              >
                Cancel
              </button>
              <button
                onClick={saveUrlsFromModal}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || saving}
              >
                Save URLs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


